import React, {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useState,
} from 'react'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  FormControlLabel,
  Grid,
  Radio,
  RadioGroup,
} from '@mui/material'
import { makeStyles } from '@mui/styles'
import classNames from 'classnames'
import * as R from 'ramda'

import i18n from '../../locales/i18n'
import { BillingAddress, Business, ClassesType, Nil, User } from '../../types'
import { findById, getPersonString } from '../../utils'
import { getAvailableCountry } from '../../utils/regionUtils'
import useFields from '../../utils/validation'
import ZipInput from '../form-inputs/ZipInput'
import CountrySelect from '../inputs/CountrySelect'
import PuiTextField from '../inputs/PuiTextField'
import StateSelect from '../inputs/StateSelect'

const useStyles = makeStyles(
  theme => ({
    content: {
      padding: theme.spacing(1, 3, 3),
    },
    leftColumn: {
      paddingRight: theme.spacing(1.5),
    },
    rightColumn: {
      paddingLeft: theme.spacing(1.5),
    },
    paddingRight: {
      [theme.breakpoints.up('sm')]: {
        paddingRight: theme.spacing(3),
      },
    },
    alignEnd: {
      alignSelf: 'flex-end',
    },
    accordionRoot: {
      margin: theme.spacing(2, 3, 0),
      padding: theme.spacing(1),
    },
    accordionExpanded: {
      margin: '16px 24px 0 !important',
      padding: theme.spacing(1),
    },
    accordionDetailsRoot: {
      padding: 0,
    },
    accordionSummaryRoot: {
      height: '24px',
      padding: 0,
      minHeight: '24px !important',
    },
    labelRoot: {
      height: 24,
    },
    label: {
      color: theme.colors.secondaryText,
    },
  }),
  { name: 'BillingAddressForm' },
)

export interface BillingAddressFormProps {
  readonly billingAddresses?: BillingAddress[]
  readonly business?: Business
  readonly classes?: ClassesType<typeof useStyles>
  readonly client?: User
  readonly currentBillingAddress: BillingAddress | Nil
}

export interface BillingAddressFormHandle {
  getBillingAddress: () => BillingAddress
  validate: () => boolean
}

const BillingAddressForm = forwardRef<
BillingAddressFormHandle,
BillingAddressFormProps
>(function BillingAddressForm(
  {
    client,
    billingAddresses = [],
    business,
    currentBillingAddress = {},
    classes: classesProp,
  },
  ref,
) {
  const classes = useStyles({ classes: classesProp })

  const selectedByDefaultAddress: BillingAddress = R.isEmpty(
    currentBillingAddress,
  )
    ? R.head(billingAddresses) || {}
    : (currentBillingAddress as BillingAddress)

  const [expanded, setExpanded] = useState(false)
  const [selectedBillingAddress, setSelectedBillingAddress] =
    useState<BillingAddress>(selectedByDefaultAddress)

  const { fields, validate, reset } = useFields(
    [
      {
        name: 'firstName',
        label: i18n.t('Common:FORMS.FIRST_NAME'),
        validators: ['required'],
        initialValue: client?.firstName,
      },
      {
        name: 'lastName',
        label: i18n.t('Common:FORMS.LAST_NAME'),
        validators: ['required'],
        initialValue: client?.lastName,
      },
      {
        name: 'address',
        label: i18n.t('Common:FORMS.ADDRESS'),
        validators: ['required'],
        initialValue: client?.address,
      },
      {
        name: 'apartmentNumber',
        label: i18n.t('Common:FORMS.APARTMENT_NUMBER'),
        initialValue: client?.suite,
      },
      {
        name: 'country',
        label: i18n.t('Regions:TERRITORY.COUNTRY'),
        validators: ['required'],
        initialValue: getAvailableCountry(client?.country || business?.country),
      },
      {
        name: 'city',
        label: i18n.t('Regions:TERRITORY.CITY'),
        validators: ['required'],
        initialValue: client?.city,
      },
      {
        name: 'state',
        label: i18n.t('Regions:TERRITORY.STATE'),
        validators: ['required'],
        type: 'select',
        initialValue: client?.state,
      },
      {
        name: 'zip',
        label: i18n.t('Common:FORMS.ZIP_CODE'),
        validators: ['required', 'zip'],
        initialValue: client?.zip,
      },
    ],
    false,
  )

  const {
    firstName,
    lastName,
    address,
    apartmentNumber,
    country,
    city,
    state,
    zip,
  } = fields

  useImperativeHandle(ref, () => ({
    validate,
    getBillingAddress: () => {
      const billingAddress = {
        firstName: firstName.value,
        lastName: lastName.value,
        street: address.value,
        apartmentNumber: apartmentNumber.value,
        country: country.value,
        city: city.value,
        state: state.value,
        zip: zip.value,
      }

      return selectedBillingAddress.id
        ? { ...selectedBillingAddress, ...billingAddress }
        : billingAddress
    },
  }))

  const preselectAllFields = (currentAddress: BillingAddress) => {
    const formattedAddress = {
      ...currentAddress,
      address: currentAddress.street,
    }

    Object.keys(formattedAddress).map(
      // @ts-ignore
      key => fields[key]?.setValue(formattedAddress[key]),
    )
  }

  useEffect(() => {
    if (!R.isEmpty(selectedByDefaultAddress)) {
      setSelectedBillingAddress(selectedByDefaultAddress)
      preselectAllFields(selectedByDefaultAddress)
    }
  }, [selectedByDefaultAddress])

  const updateSelectedBillingAddress = (value?: string) => {
    const selectedAddress = findById(value, billingAddresses)
    if (selectedAddress) {
      setSelectedBillingAddress(selectedAddress)
      preselectAllFields(selectedAddress)
    } else {
      setSelectedBillingAddress({})
      reset()
    }
  }

  const addressForLabel = R.head(billingAddresses)
  const label = (
    <FormControlLabel
      classes={{
        label: classes.label,
      }}
      control={
        <Radio
          checked={selectedBillingAddress.id === addressForLabel?.id}
          value={addressForLabel?.id}
          onChange={() => updateSelectedBillingAddress(addressForLabel?.id)}
        />
      }
      label={i18n.t<string>('Common:FORMS.USE_SAVED_ADDRESS_FOR_CLIENT', {
        person: getPersonString(addressForLabel),
      })}
      onClick={event => event.stopPropagation()}
      onFocus={event => event.stopPropagation()}
    />
  )

  const expandedBillingAddressed = [...billingAddresses.slice(1)]

  return (
    <>
      {!R.isEmpty(billingAddresses) && (
        <Accordion
          classes={{
            root: classes.accordionRoot,
            expanded: classes.accordionExpanded,
          }}
          expanded={expanded}
          onChange={() => setExpanded(!expanded)}
        >
          <AccordionSummary
            classes={{ root: classes.accordionSummaryRoot }}
            expandIcon={<ExpandMoreIcon />}
          >
            {label}
          </AccordionSummary>
          <AccordionDetails classes={{ root: classes.accordionDetailsRoot }}>
            <RadioGroup
              value={selectedBillingAddress.id || ''}
              onChange={(_, value) => updateSelectedBillingAddress(value)}
            >
              {expandedBillingAddressed.map(person => (
                <FormControlLabel
                  classes={{ root: classes.labelRoot, label: classes.label }}
                  control={<Radio />}
                  key={person.id}
                  label={i18n.t<string>(
                    'Common:FORMS.USE_SAVED_ADDRESS_FOR_CLIENT',
                    { person: getPersonString(person) },
                  )}
                  value={person.id}
                />
              ))}
              <FormControlLabel
                classes={{ root: classes.labelRoot, label: classes.label }}
                control={<Radio />}
                label={i18n.t<string>('Common:FORMS.ENTER_ADDRESS')}
                value=""
              />
            </RadioGroup>
          </AccordionDetails>
        </Accordion>
      )}
      <Grid container className={classes.content}>
        <Grid item className={classes.leftColumn} xs={6}>
          <PuiTextField
            fullWidth
            field={firstName}
            inputProps={{ maxLength: 100 }}
            label={`${firstName.label}*`}
          />
        </Grid>
        <Grid item className={classes.rightColumn} xs={6}>
          <PuiTextField
            fullWidth
            field={lastName}
            inputProps={{ maxLength: 100 }}
            label={`${lastName.label}*`}
          />
        </Grid>
        <Grid item xs={12}>
          <PuiTextField
            field={address}
            inputProps={{ maxLength: 100 }}
            label={`${address.label}*`}
          />
        </Grid>
        <Grid item className={classes.leftColumn} xs={9}>
          <PuiTextField
            field={apartmentNumber}
            inputProps={{ maxLength: 100 }}
            label={apartmentNumber.label}
          />
        </Grid>
        <Grid
          item
          className={classNames(classes.rightColumn, classes.alignEnd)}
          xs={3}
        >
          <CountrySelect required field={country} />
        </Grid>
        <Grid item className={classes.paddingRight} sm={5} xs={12}>
          <PuiTextField
            fullWidth
            field={city}
            inputProps={{ maxLength: 100 }}
            label={`${city.label}*`}
            type="text"
          />
        </Grid>
        <Grid
          item
          className={classNames(classes.paddingRight, classes.alignEnd)}
          sm={4}
          xs={6}
        >
          <StateSelect required country={country.value} field={state} />
        </Grid>
        <Grid item className={classes.rightColumn} sm={3} xs={6}>
          <PuiTextField
            fullWidth
            InputProps={{
              inputComponent: ZipInput,
              inputProps: { country: country.value },
            }}
            field={zip}
            label={`${zip.label}*`}
            type="text"
          />
        </Grid>
      </Grid>
    </>
  )
})

export default BillingAddressForm
